import { useContext, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dynamic from "next/dynamic";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { InternalButton, ExternalButton } from "@sharedComponents/buttons";

import { pluralizeWord } from "@stadium/helpers/string";

import { useDownloadInvoice } from "@stadium/checkout/src/hooks/use-fetch-order-invoice";
import useGetVariantDetails from "@hooks/cart/use-get-variant-details";
import { useWorkspaceContext } from "@contexts/workspace-context";

import CartProductListing from "@sharedComponents/cart/send-to-many/cart-product-listing";

import {
  getIsPricelessShop,
  getIsStorePointsMigrationAllowed,
  getStoreCurrencyCode,
  getStoreBalance,
  getStoreCurrencyDetails,
  getStoreCurrencyName,
  getIsKitStore,
} from "@selectors/store";

import { setStoreBalance } from "@slices/store";

import { isGiftCardProduct } from "@helpers/products/types";
import { generateLineItemsMappedData } from "@helpers/orders/cart";

import { externalRoutes } from "@constants/external-routes";
import { getWorkspaceShopTrackOrderRoute } from "@stadium/constants/routes";

import {
  ORDERS_TARGET,
  HOME_TARGET,
  RECEIPT_TARGET,
  TRACKING_LINK_TARGET,
} from "@constants/analytics/checkout";
import { ANALYTICS_EVENT_ACTION_DOWNLOADED } from "@stadium/services/analytics/constants/default";

const TransferPointsModal = dynamic(
  () => import("@stadium/shared/user-funds-detail/transfer-points"),
  {
    ssr: false,
  },
);
const PompAddOnSection = dynamic(() =>
  import("@stadium/checkout/src/components/pomp-add-on-section"),
);

import styles from "./main.module.scss";

const OrderConfirmationExtraInfo = ({ orderNumber, order }) => {
  const { loading, fetchFile } = useDownloadInvoice(orderNumber);
  const { getDashboardUrlWithCurrentWorkspaceSlug, workspaceSlug } = useWorkspaceContext();
  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const storeBalance = useSelector(getStoreBalance);
  const storeCurrencyName = useSelector(getStoreCurrencyName);
  const shopCurrencyCode = useSelector(getStoreCurrencyCode);
  const storeDetails = useSelector(getStoreCurrencyDetails);
  const dispatch = useDispatch();
  const isPricelessShop = useSelector(getIsPricelessShop);
  const isKitStore = useSelector(getIsKitStore);

  const handlePointsTransferSuccess = () => {
    const storePayload = {
      balance: 0,
    };
    dispatch(setStoreBalance(storePayload));
  };

  const pointMigrationAllowed = useSelector(getIsStorePointsMigrationAllowed);
  const [isTransferPointModalOpen, setIsTransferPointModalOpen] = useState(false);
  const { lineItems, productSets, medleyItemSets, hasPomp, pompFee, adjustmentCurrencyId } = order;
  const { productsWithVariantDetails, variantsMapping, variantDataloading, variantData } =
    useGetVariantDetails({
      lineItems,
      medleySets: medleyItemSets,
      productSets,
    });

  const mappedLineItems = useMemo(() => {
    const mappedItems = generateLineItemsMappedData({
      lineItems,
      variantsMapping,
      variantDataloading,
    });
    return mappedItems.map((item) => {
      const lineItemDigitalAssetData = variantData?.digitalAssets?.nodes?.find(
        (asset) => +asset.id === item.lineItemCustomization?.logoId,
      );
      return { ...item, digitalAssetsData: lineItemDigitalAssetData };
    });
  }, [lineItems, variantsMapping, variantDataloading, variantData?.digitalAssets]);

  const { lineItemsWithDetails, medleySetsWithDetails, productSetItemsWithDetails } =
    productsWithVariantDetails;
  const hasGiftCards = lineItemsWithDetails.some(({ intent }) => isGiftCardProduct(intent));
  const renderCtaFooter = () => {
    return (
      <div className={styles.ctaGroup}>
        <ExternalButton
          href={getDashboardUrlWithCurrentWorkspaceSlug(externalRoutes.purchases())}
          styleType="primary-outline"
          className={styles.goToBtn}
          target="_self"
          eventLocationAnalytics={{
            track: true,
            target: ORDERS_TARGET,
          }}
        >
          Go to my orders
        </ExternalButton>

        <ExternalButton
          href={getDashboardUrlWithCurrentWorkspaceSlug(externalRoutes.home())}
          styleType="primary"
          target="_self"
          eventLocationAnalytics={{
            track: true,
            target: HOME_TARGET,
          }}
        >
          {isMobileDevice ? "Stadium home" : "Go to stadium home"}
        </ExternalButton>
      </div>
    );
  };

  return (
    <div className={styles.orderSummaryWrap}>
      <div className={styles.dividerBorder}></div>
      <div className={styles.orderLeft}>
        <div className={styles.openOrdeerListInfo}>
          <aside className={styles.starIcon}></aside>
          <InternalButton
            disabled={loading}
            onClick={fetchFile}
            eventLocationAnalytics={{
              track: true,
              target: RECEIPT_TARGET,
              action: ANALYTICS_EVENT_ACTION_DOWNLOADED,
            }}
            styleType="link"
            className={styles.downloadReceipt}
          >
            <span className={styles.downloadingReceipt}>
              {loading ? "Downloading Receipt" : "Download Receipt"}
            </span>
          </InternalButton>
        </div>

        {hasGiftCards && (
          <div className={styles.openOrdeerListInfo}>
            <aside className={styles.starIcon}></aside>
            <div className={styles.heading2}>
              If you’ve purchased a digital experience or gift card, your experience redemption code
              will be emailed to you{" "}
              {isPricelessShop ? "after the sender approves" : "in 48-72 hours"}.
            </div>
          </div>
        )}

        <div className={styles.openOrdeerListInfo}>
          <aside className={styles.starIcon}></aside>
          <div className={styles.heading2}>
            Check your email for updates on your shipping or track your shipping&nbsp;
            <ExternalButton
              styleType="link"
              href={getWorkspaceShopTrackOrderRoute(orderNumber, workspaceSlug)}
              className={styles.hereBtn}
              eventLocationAnalytics={{
                track: true,
                target: TRACKING_LINK_TARGET,
              }}
            >
              here.
            </ExternalButton>
          </div>
        </div>

        {storeBalance && pointMigrationAllowed ? (
          <div className={`${styles.openOrdeerListInfo} ${styles.bgYellow}`}>
            <aside className={styles.starIcon}></aside>
            <div className={styles.heading2}>
              You have {storeBalance} {storeCurrencyName} remaining in your shop. You can transfer
              these {storeCurrencyName}&nbsp;to Stadium Points&nbsp;
              <InternalButton
                styleType="link"
                onClick={() => setIsTransferPointModalOpen(true)}
                className={styles.hereBtn}
              >
                here.
              </InternalButton>
            </div>
          </div>
        ) : (
          <></>
        )}

        {isMobileDevice ? null : renderCtaFooter()}
      </div>
      <div className={styles.orderRight}>
        <div className={styles.itemsWrapper}>
          <div className={`${styles.yourItemHeading}`}>
            <div className={styles.headingText}>
              Your{" "}
              {pluralizeWord("item", {
                count: order.itemCount,
              })}
            </div>
          </div>
          {!variantDataloading && (
            <CartProductListing
              lineItems={mappedLineItems}
              productSets={productSetItemsWithDetails}
              medleySets={medleySetsWithDetails}
              commonProps={{
                variantsMapping,
                hidePrices: order.bundle || order.sendToMany,
                orderCurrencyCode: order.currency,
                hideLink: isKitStore,
              }}
            />
          )}
        </div>

        {hasPomp && !!pompFee && (
          <PompAddOnSection
            pompFee={pompFee}
            feeCurrencyCode={shopCurrencyCode}
            adjustmentCurrencyId={adjustmentCurrencyId}
          />
        )}
      </div>
      {isTransferPointModalOpen && (
        <TransferPointsModal
          closeModal={() => setIsTransferPointModalOpen(false)}
          data={{ ...storeDetails, handlePointsTransferSuccess, workspaceSlug }}
        />
      )}
      {isMobileDevice ? renderCtaFooter() : null}
    </div>
  );
};

export default OrderConfirmationExtraInfo;
